import React, { useCallback, useContext, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useHeaderControls } from '@fs/zion-root'
import { colors, Divider, MarginBox } from '@fs/zion-ui'
import { Link, useLocation, useRouteMatch } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import { i18n } from '@fs/zion-locale'
import { css } from '@emotion/core'
import { boolAttr, useResizeObserver } from '@fs/zion-frontend-friends'
import Subnav from '@fs/zion-tree-subnav-legacy'
import { DarkModeContext } from '../../DarkModeProvider'

const navBarCss = css`
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;
  white-space: nowrap;
  scroll-behavior: smooth;
  line-height: 2.5rem;

  /* Hide menu when printing */
  @media print {
    display: none;
  }

  /* Hide menu when in full screen mode */
  transition: 0.3s ease;
  &[data-hide] {
    transform: translateY(calc(-1 * var(--navigation-tabs-height)));
    margin-bottom: calc(-1 * var(--navigation-tabs-height));
  }
`

// Not using the Zion UI Row here because list items should be direct children of ul
const rowCss = css`
  display: flex;
  gap: 4px;

  /** Reset ul/li styles */
  list-style: none;
  margin: 0;
  padding: 0;

  & > li {
    padding: 0;
    margin: 0;
  }
`

const linkCss = css`
  color: ${colors.gray100};
  border-radius: 4px;
  font-size: 14px;
  padding: 4px 10px;

  [lang|='zh'] &,
  [lang|='ja'] &,
  [lang|='ko'] & {
    font-size: 16px;
  }

  &:hover,
  &:focus,
  &[data-selected] {
    background: ${colors.green03};
    color: ${colors.gray100};

    &[theme='night'] {
      background: ${colors.green90};
      color: ${colors.gray00};
    }
  }
  &:hover {
    text-decoration: underline;
  }
`

const untranslatedBooksLangs = [
  'zh',
  'zh-hans',
  'ja',
  'ko',
  'th',
  'da',
  'hu',
  'nl',
  'pl',
  'sv',
  'sk',
  'fi',
  'mn',
  'bg',
  'km',
  'id',
  'ro',
  'sm',
  'to',
  'vi',
  'fj',
  'sq',
  'cs',
  'no',
  'ar',
]

export default function NavigationTabs({ navRef }) {
  const { pathname, search } = useLocation()

  const showTreeSubnav = pathname.includes('tree') && search.includes('from=tree')

  return <div ref={navRef}>{showTreeSubnav ? <Subnav page="find" /> : <NavigationTabsInner />}</div>
}

NavigationTabs.propTypes = {
  navRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
}

function NavigationTabsInner() {
  const { t } = useTranslation()

  // If the header goes into full screen mode, gracefully hide the header
  const headerControls = useHeaderControls()
  const headerContentRef = useRef()
  const setHeaderHeight = useCallback(() => {
    // We use this CSS variable in the "hide header" transition
    document.documentElement.style.setProperty(
      '--navigation-tabs-height',
      `${headerContentRef.current?.clientHeight}px`
    )
  }, [])
  useResizeObserver(headerContentRef, setHeaderHeight)

  const navLinks = useNavLinks()
  const { theme } = useContext(DarkModeContext)

  return (
    <>
      <MarginBox
        marginX="xxs"
        as="nav"
        aria-label={t('search.tab.secondary', 'Secondary')}
        css={navBarCss}
        data-hide={boolAttr(!headerControls || !headerControls?.isVisible)}
        ref={headerContentRef}
      >
        <ul css={rowCss}>
          {navLinks.map(({ path, selected, name }) => (
            <li key={path}>
              <Link
                to={path}
                external
                wayfinding
                noLinkStyles
                css={linkCss}
                data-night={boolAttr(theme === 'night')}
                data-selected={boolAttr(selected)}
              >
                {name}
              </Link>
            </li>
          ))}
        </ul>
      </MarginBox>
      <Divider />
    </>
  )
}

function useNavLinks() {
  const { t } = useTranslation()
  // Books and Wiki are hidden for CJK languages at the request of FS CJK specialists
  const hideBooksLangs = untranslatedBooksLangs.includes(i18n.language)
  const { pathname } = useLocation()
  const genArkMatch = useRouteMatch('/ark:/61903/2::arkPath')
  const simpleSearchMatch = useRouteMatch('/search/simple-search')
  return useMemo(() => {
    const tabArray = ['/full-text', '/tree', '/genealogies', '/catalog']
    const defaultSearchTabSelected =
      !simpleSearchMatch && !genArkMatch && !tabArray.some((page) => pathname.includes(page))
    return [
      {
        path: '/search/',
        name: t('search.tab.records', 'Records'),
        selected: defaultSearchTabSelected,
      },
      {
        path: '/search/full-text',
        name: t('search.tab.fullText', 'Full Text'),
        selected: pathname.includes('/full-text'),
      },
      // this app does not own the /records/images route, so this component is not loaded on that page and cannot be "selected"
      {
        path: '/records/images',
        name: t('search.tab.images', 'Images'),
        selected: false,
      },
      {
        path: '/search/tree',
        name: t('search.tab.tree', 'Family Tree'),
        selected: pathname.includes('/tree'),
      },
      {
        path: '/search/genealogies',
        name: t('search.tab.genealogies', 'Genealogies'),
        selected: genArkMatch || pathname.includes('/genealogies'),
      },
      {
        path: '/search/catalog',
        name: t('search.tab.catalog', 'Catalog'),
        selected: pathname.includes('/catalog'),
      },
      ...(hideBooksLangs
        ? []
        : [
            // this app does not own the library or wikilink pages, so this component is not loaded on those pages and cannot be "selected"
            {
              path: 'https://www.familysearch.org/library/books/',
              name: t('search.tab.books', 'Books'),
              selected: false,
            },
            {
              path: t('search.tab.wikiLink', '/en/wiki/Main_Page'),
              name: t('search.tab.wiki', 'Wiki'),
              selected: false,
            },
          ]),
    ]
  }, [pathname, simpleSearchMatch, genArkMatch, hideBooksLangs, t])
}
